import { useToast } from '@ui/toasts/useToastManager'
import {
  Deal,
  isPackageRestricted,
  PackageConfig,
  PackageType,
  RelationCondition,
  RemoteConfig,
  StrapiID,
} from '@ca/common/types'
import { useOrder, useOrderState } from '@order/hooks/useOrderState'
import {
  useCurrentPackageType,
  usePackageUpgrades,
} from '@order/hooks/usePackages'
import checkOrderValues, {
  validateOrderValues,
} from '@order/utils/checkOrderValues'
import { debugConditions, debugOrder } from '@order/utils/getDebugLog'
import { DEFAULT_FLOW_NAME, getFlow } from '@order/utils/getFlow'
import { getNextStepHref } from '@order/utils/getNextStepHref'
import { VariablesUtils } from '@order/utils/OrderVariables'
import {
  getPackageVariant,
  getUpgrades,
} from '@order/utils/packages/PackagesUtils'
import { ValidationResult } from '@order/utils/validateCondition'
import AppConfig from 'config/AppConfig'
import { router } from 'expo-router'
import { useAvatarSessionInitialize } from 'features/avatar/speech/useAvatarSession'
import useAvatarFlow from 'features/avatar/useAvatarFlow'
import { useConfigCondition } from 'features/config/useConfigCondition'
import useRecentPrescription from 'hooks/useRecentPrescription'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, { getSessionDealContext } from 'hooks/useSession'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { tlFallback } from 'utils/i18nUtils'
import useBundleType from './useBundleType'
import { getLocalizedDealById } from './useDeal'
import { useDealsById, useLocalizedDeals } from './useDeals'
import useIsTempleRequired from './useIsTempleRequired'
import { useLastOrderDefaults } from './useSaveOrderDefaults'
import { getCurrentOrderValues, getPackageValues } from './values/OrderValues'

const resolvePackage = (
  packageOrId: PackageConfig | StrapiID | undefined,
  config: Pick<RemoteConfig, 'packages'> | null
) => {
  if (typeof packageOrId === 'string' || typeof packageOrId === 'number') {
    return config?.packages[packageOrId]
  }
  return packageOrId
}

const useStartOrderConfig = () => {
  const { t, i18n } = useTranslation(['toasts', 'deals-toasts'])
  const { initialPackage, selectedDeal, packages, baseDeal, basePackage } =
    usePackageUpgrades()
  const config = useRemoteConfig()
  const order = useOrder()
  const deals = useLocalizedDeals()
  const dealsById = useDealsById()
  const dealLang = AppConfig.dealLang || i18n.language
  const setPackage = useOrderState((s) => s.setPackage)
  const setItemFlowName = useOrderState((s) => s.setItemFlowName)
  const { bundleType, dealUpgradeKey } = useBundleType()
  const hasRecentPrescription = useRecentPrescription()
  const { lastUsedPrescriptionId } = useLastOrderDefaults()
  const hasLastUsedPrescription = !!lastUsedPrescriptionId
  const isAvatarCallEnabled =
    useConfigCondition('avatar:isCallEnabled') && AppConfig.avatar.enabled
  const setDeal = useSession((s) => s.setDeal)
  const checkIsTempleRequired = useIsTempleRequired()
  const { isAvatarFlow } = useAvatarFlow()
  const invalidateStaff = useSession((s) => s.invalidateStaff)

  const activeItem = useMemo(
    () => order.items.find((i) => i.id === order.activeItemId),
    [order.items, order.activeItemId]
  )
  const flowName = useMemo(() => activeItem?.flowName, [activeItem])

  const { initialize: initializeAvatar } = useAvatarSessionInitialize()

  const isAvatarFlowEnabled = isAvatarFlow && bundleType === 'base'

  const currentPackageType = useCurrentPackageType()

  const startNextFrame = () => {
    debugOrder.log('useStartOrderConfig.startNextFrame')
    if (!config) {
      debugOrder.warn('useStartOrderConfig.startNextFrame missing config')
      return
    }
    const orderValues = getCurrentOrderValues({ bundleType })

    const flow = getFlow(config, flowName)
    const frameStepId =
      flow.stepModes.find((m) => m.mode === 'frame')?.initialStep ||
      // Fallback to to first mode step if no frame step is found
      flow.stepModes[0]?.initialStep
    const href = getNextStepHref({
      config,
      orderItemId: order.activeItemId,
      states: {},
      mode: 'frame',
      stepId: frameStepId,
      values: orderValues.values,
      refraction: orderValues.refraction,
      bundleType,
      endPath: undefined,
    })
    router.push(href)
  }

  const toStep = (
    item: PackageConfig,
    deal?: Deal,
    flowNameOverride?: string
  ) => {
    debugOrder.log('useStartOrderConfig.toStep', {
      item,
      deal,
    })
    if (!config) {
      debugOrder.warn('useStartOrderConfig.toStep missing config')
      return
    }

    const orderValues = getCurrentOrderValues({ bundleType })
    const variant = getPackageVariant(item, config, orderValues)

    if (deal) {
      setDeal(
        getSessionDealContext(deal, {
          basePackageId: initialPackage?.id || item.id,
          activePackageId: item.id,
        }),
        dealUpgradeKey
      )
    }

    const selected = setPackage(item, {
      variant,
      region: config.region,
      frames: deal?.frames || selectedDeal?.frames || 1,
      overrides: VariablesUtils.fromOverrides(
        variant ? variant.overrides : item.overrides
      ),
      bundle: bundleType,
    })

    const values = {
      ...orderValues.values,
      // Update values from selected package
      ...getPackageValues(selected),
      packageColor: variant?.color || item.color || undefined,
    }

    const flowNameResolved = flowNameOverride ?? flowName
    const flow = getFlow(config, flowNameResolved)

    const href = getNextStepHref({
      config,
      orderItemId: order.activeItemId,
      states: {},
      stepId: flow.stepModes[0]?.initialStep,
      flowName: flowNameResolved,
      values,
      refraction: orderValues.refraction,
      bundleType,
      endPath: undefined,
    })

    router.push(href)

    // Initialize avatar session if needed
    if (flow.isSelfService && orderValues.refraction && isAvatarCallEnabled) {
      initializeAvatar(orderValues.refraction)
    }
  }

  const toPackages = (deal?: Deal, packageOrId?: PackageConfig | StrapiID) => {
    const pckg = resolvePackage(packageOrId, config)
    const packageConfig = pckg || initialPackage
    const activeDeal = deal || baseDeal

    const effectiveFlowName =
      deal !== undefined ? deal.settings?.flow ?? DEFAULT_FLOW_NAME : flowName
    if (
      deal !== undefined &&
      effectiveFlowName !== flowName &&
      order.activeItemId
    ) {
      setItemFlowName(order.activeItemId, effectiveFlowName)
    }

    const type = packageConfig?.type
    const isValidFH = checkFh(type)

    if (!isValidFH) {
      showFhToast()
      return
    }

    if (deal && pckg) {
      const basePackageId =
        deal.id === baseDeal?.id && basePackage ? basePackage.id : pckg.id
      setDeal(
        getSessionDealContext(deal, {
          basePackageId,
          activePackageId: pckg.id,
        }),
        dealUpgradeKey
      )
    }

    if (packageConfig && isAvatarFlowEnabled) {
      toStep(packageConfig, undefined, effectiveFlowName)
      return
    }

    const upgradeDeals =
      deals &&
      activeDeal?.upgradeDeals
        ?.map((id) => getLocalizedDealById(id, dealLang, dealsById))
        .filter((d) => !!d)

    const orderValues = getCurrentOrderValues({ bundleType })
    const upgrades = getUpgrades({
      deal: activeDeal,
      upgradeDeals: upgradeDeals || [],
      packages,
      basePackage: packageConfig,
      values: orderValues,
    })
    debugOrder.log('useStartOrderConfig.toPackages', {
      upgrades,
      deal: activeDeal,
    })

    if (!packageConfig || upgrades.length > 0) {
      router.push({
        pathname: '/configuration/packages',
        params: { bundleType },
      })
      return
    }
    toStep(packageConfig, undefined, effectiveFlowName)
  }

  const checkCondition = (condition?: RelationCondition) => {
    const orderValues = getCurrentOrderValues({ bundleType })
    const res = checkOrderValues(condition, orderValues, config)
    if (!res) {
      debugConditions.log('checkCondition failed', {
        condition: condition && config?.conditions[condition.value],
        orderValues,
        res,
      })
    }
    return res
  }

  const checkUpsell = () => {
    // For now disable upsell for avatar flow
    if (isAvatarFlow) {
      return false
    }

    const vfUpsellCondition =
      config?.region.conditions?.vfUpsell || config?.region.vfUpsellCondition
    if (!vfUpsellCondition) {
      return false
    }
    return checkCondition(config.region.vfUpsellCondition)
  }

  const checkFh = (type?: PackageType) => {
    const packageType = type || currentPackageType
    const condition =
      packageType === 'sv'
        ? config?.region.conditions?.fh || config?.region.fhCondition
        : config?.region.conditions?.fhVf || config?.region.fhVfCondition

    if (!condition) {
      return true
    }

    return checkCondition(condition)
  }

  const checkVF = (type?: PackageType) => {
    const packageType = type || currentPackageType

    if (packageType === 'sv') {
      return {
        valid: true,
        type: 'sv',
      }
    }

    const orderValues = getCurrentOrderValues({ bundleType })

    if (packageType === 'office' && config?.region.conditions?.office) {
      const valid = checkOrderValues(
        config?.region.conditions?.office,
        orderValues,
        config
      )
      return {
        valid,
        type: 'office',
      }
    }

    const valid = checkOrderValues(
      config?.region.conditions?.vf || config?.region.vfCondition,
      orderValues,
      config
    )
    return {
      valid,
      type: 'vf',
    }
  }

  const checkPackageCondition = () => {
    if (!basePackage || !basePackage.condition) {
      return {
        result: true,
      } satisfies ValidationResult
    }

    const orderValues = getCurrentOrderValues({ bundleType })

    return validateOrderValues(
      config?.conditions[basePackage.condition],
      orderValues,
      config
    )
  }

  const toast = useToast()

  const showFhToast = () => {
    const fhKey = currentPackageType === 'sv' ? 'fh.sv' : 'fh.vf'
    toast({
      title: t(`toasts:error.${fhKey}.title`),
      text: t(`toasts:error.${fhKey}.desc`),
      variant: 'error',
    })
  }

  const startConfig = () => {
    const isValidFH = checkFh()

    if (!isValidFH) {
      showFhToast()
      return
    }
    const isValidVf = checkVF()

    if (!isValidVf.valid) {
      toast({
        title: t(`toasts:error.${isValidVf.type}.title`),
        text: t(`toasts:error.${isValidVf.type}.desc`),
        variant: 'error',
      })
      return
    }
    const packageCondition = checkPackageCondition()
    if (!packageCondition.result) {
      toast({
        title: tlFallback(
          i18n,
          `deals-toasts:error.${packageCondition.errorKey}.title`,
          'toasts:error.packageCondition.title'
        ),
        text: tlFallback(
          i18n,
          `deals-toasts:error.${packageCondition.errorKey}.desc`,
          'toasts:error.packageCondition.desc'
        ),
        variant: 'error',
      })
      return
    }

    if (isAvatarFlow && bundleType === 'base') {
      // Mark staff as invalidated to force revalidation
      invalidateStaff()
      router.push('/configuration/deal/recommended')
      return
    }

    const canUpsell = checkUpsell()
    if (
      !canUpsell ||
      initialPackage?.type !== 'sv' ||
      isPackageRestricted('sv', 'vf', baseDeal?.extraRestrictType)
    ) {
      toPackages()
      return
    }

    if (
      // Here we always check base deal to make sure we handle back
      // Either we can upgrade package type or we have a vf deal
      baseDeal?.vfDeal ||
      baseDeal?.packages.some((id) => packages[id]?.type === 'vf')
    ) {
      router.push({
        pathname: '/configuration/upsell',
        params: { bundleType },
      })
      return
    }

    toPackages()
  }

  const goToFrameSettings = useCallback(
    (frameId: StrapiID) => {
      // Should we skip frame settings if we already have this frame in the order?
      router.push({
        pathname: `/frame/settings/${frameId}`,
        params: { bundleType },
      })
    },
    [bundleType]
  )

  const goToPrescription = () => {
    const bundleItems = order.items.filter((i) => i.bundleType === bundleType)

    if (order.prescriptionId && bundleItems.some((i) => i.isCompleted)) {
      // We prescription and have some frames completed so we can start next frame
      startNextFrame()
      return
    }

    if (bundleType !== 'base') {
      startConfig()
      return
    }
    router.push(
      hasRecentPrescription || hasLastUsedPrescription
        ? '/vision/values'
        : '/vision/source'
    )
    return
  }

  const goToStock = useCallback(() => {
    router.push({
      pathname: `/frame/stock/${order.activeItemId}`,
      params: { bundleType },
    })
  }, [bundleType, order.activeItemId])

  const goToTempleSelect = () => {
    const isValidFH = checkFh()
    if (!isValidFH) {
      showFhToast()
      return
    }

    const currentItem = order.items.find((i) => i.id === order.activeItemId)

    // We need frame to check if temple is required
    if (!currentItem?.frame) {
      // Missing frame
      return
    }

    const isTempleRequired = checkIsTempleRequired(currentItem.frame)

    if (isTempleRequired) {
      router.push({
        pathname: '/frame/temple/select',
        params: { bundleType },
      })
      return
    }
    // Temple is not required so we can go to prescription
    goToStock()
  }

  return {
    goToFrameSettings,
    goToPrescription,
    goToStock,
    goToTempleSelect,
    startConfig,
    toPackages,
    // toInterview,
    toStep,
    checkFh,
    checkVF: (type?: PackageType) => checkVF(type)?.valid,
  }
}

export default useStartOrderConfig
